import { Injectable } from '@angular/core';
import { SbNavpointModel } from '@ngx-seatback/layout';
import { Permission } from './api/permission';
import { TranslateService } from '@ngx-translate/core';
import { RoutingID } from '../routes/sb-routing-enum';
import { SbIcons } from '@ngx-seatback/icons';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class NavpointsService {
  clientID: any = null;

  navMain: SbNavpointModel[] = null;
  navFooter: SbNavpointModel[] = null;

  dashboard: SbNavpointModel = null;
  content: SbNavpointModel = null;
  network: SbNavpointModel = null;
  datasets: SbNavpointModel = null;
  reporting: SbNavpointModel = null;

  constructor(
    private translate: TranslateService,
    private userService: UserService
  ) {}

  getNavpoints(): Map<string, SbNavpointModel[]> {
    this.dashboard = {
      name: this.translate.instant('nav.dashboard'),
      icon: SbIcons.home,
      link: RoutingID.home,
      // singlePointSmall: true, // remove small point on navbar
    };

    this.content = {
      name: this.translate.instant('nav.content'),
      icon: SbIcons.megaphone,
      link: RoutingID.content,
      minPermissions: [
        Permission.campaign_list,
        Permission.planning_list,
        Permission.medium_list,
        Permission.medium_group_list,
        Permission.template_source_list,
        Permission.template_store_list,
        Permission.product_list,
        Permission.content_share_list,
      ],
      children: [
        {
          name: this.translate.instant('nav.assignment'),
          icon: SbIcons.assignments,
          minPermissions: [Permission.campaign_list, Permission.planning_list, Permission.content_share_list, Permission.targeting_list],
          link: RoutingID.assignment,
          tooltipText: this.translate.instant('nav.campaigns.description'),
          children: [
            {
              name: this.translate.instant('nav.campaigns.inventory'),
              icon: SbIcons.checkList,
              link: RoutingID.inventoryOverview,
              minPermissions: [],
            },
            /*
            TODO comment in when calendar done
            {
              name: this.translate.instant('nav.reservations'),
              icon: SbIcons.planningSelection,
              link: RoutingID.reservations,
              minPermissions: [],
            },

             */
            {
              name: this.translate.instant('nav.campaigns.plannings'),
              icon: SbIcons.plannings,
              link: RoutingID.plannings,
              minPermissions: [Permission.planning_list],
            },
            {
              name: this.translate.instant('nav.campaigns.management'),
              icon: SbIcons.management,
              link: RoutingID.campaigns,
              minPermissions: [Permission.campaign_list],
            },
            {
              name: this.translate.instant('nav.campaigns.contentShare'),
              icon: SbIcons.management,
              link: RoutingID.contentShare,
              minPermissions: [Permission.content_share_list],
            },
            {
              name: this.translate.instant('nav.network.targeting'),
              icon: SbIcons.targeting,
              link: RoutingID.targeting,
              minPermissions: [Permission.targeting_list],
            },
          ],
        },
        {
          name: this.translate.instant('nav.motives'),
          icon: SbIcons.motive,
          minPermissions: [
            Permission.medium_list,
            Permission.medium_group_list,
            Permission.template_source_list,
            Permission.template_store_list,
            Permission.shared_asset_list,
          ],
          tooltipText: this.translate.instant('nav.motives.description'),
          link: RoutingID.media,
          children: [
            {
              name: this.translate.instant('nav.motives.motiveExplorer'),
              icon: SbIcons.motiveExplorer,
              link: RoutingID.mediumExplorer,
              minPermissions: [Permission.medium_list],
            },
            {
              name: this.translate.instant('nav.motives.motiveGroups'),
              icon: SbIcons.motives,
              link: RoutingID.mediumGroup,
              minPermissions: [Permission.medium_group_list],
            },
            /*
                                                                        // Payment canceled, therefore nop content editor
                                                                        {
                                                                          name: this.translate.instant('nav.contentEditor'),
                                                                          icon: SbIcons.playEdit,
                                                                          link: RoutingID.contentEditor,
                                                                          minPermissions: [Permission.content_editor],
                                                                        },

                                                                         */
            {
              name: this.translate.instant('nav.assets'),
              icon: SbIcons.photos,
              link: RoutingID.assetExplorer,
              minPermissions: [Permission.shared_asset_list],
            },
            {
              name: this.translate.instant('nav.templateDatasets'),
              icon: SbIcons.datasets,
              link: RoutingID.templateDatasets,
              minPermissions: [Permission.template_data_set_list],
            },
          ],
        },
        {
          name: this.translate.instant('nav.offerings'),
          icon: SbIcons.offerings,
          minPermissions: [Permission.product_list],
          tooltipText: this.translate.instant('nav.offerings.description'),
          link: RoutingID.offerings,
          children: [
            {
              name: this.translate.instant('nav.products'),
              icon: SbIcons.product,
              link: RoutingID.products,
              minPermissions: [Permission.product_list],
            },
          ],
        },
      ],
    };

    this.network = {
      name: this.translate.instant('nav.network'),
      icon: SbIcons.network,
      link: RoutingID.network,
      minPermissions: [
        Permission.device_list,
        Permission.location_list,
        Permission.pool_list,
        Permission.visitor_tracker_list,
        Permission.playback_schedule_list,
        Permission.network_share_list,
        Permission.booking_list,
        Permission.verification_list,
        Permission.network_booking_list,
        Permission.deal_list,
      ],
      children: [
        {
          name: this.translate.instant('nav.infrastructure'),
          icon: SbIcons.networkInfrastructure,
          minPermissions: [Permission.device_list, Permission.location_list, Permission.pool_list, Permission.visitor_tracker_list],
          link: RoutingID.infrastructure,
          tooltipText: this.translate.instant('nav.network.management.description'),
          children: [
            {
              name: this.translate.instant('nav.network.devices'),
              icon: SbIcons.playerOnline,
              link: RoutingID.devices,
              minPermissions: [Permission.device_list],
            },
            {
              name: this.translate.instant('nav.network.locations'),
              icon: SbIcons.location,
              link: RoutingID.locations,
              minPermissions: [Permission.location_list],
            },
            {
              name: this.translate.instant('nav.network.pools'),
              icon: SbIcons.pools,
              link: RoutingID.pools,
              minPermissions: [Permission.pool_list],
            },
            {
              name: this.translate.instant('nav.network.tracker'),
              icon: SbIcons.tracker,
              link: RoutingID.tracker,
              minPermissions: [Permission.visitor_tracker_list],
            },
          ],
        },
        {
          name: this.translate.instant('nav.rulesAndVerification'),
          icon: SbIcons.rules,
          link: RoutingID.rulesAndVerification,
          minPermissions: [
            Permission.playback_schedule_list,
            Permission.booking_list,
            Permission.verification_list,
            Permission.network_booking_list,
            // Permission.transformation_permission
          ],
          tooltipText: this.translate.instant('nav.network.content.description'),
          children: [
            {
              name: this.translate.instant('nav.network.networkAllocation'),
              icon: SbIcons.bookings,
              link: RoutingID.networkAllocation,
              minPermissions: [Permission.booking_list, Permission.ss_nm_reporting],
            },
            {
              name: this.translate.instant('lang.globals.playoutDescriptor'),
              icon: SbIcons.bookings,
              link: RoutingID.networkBookings,
              minPermissions: [Permission.booking_list, Permission.device_export_playout],
            },
            {
              name: this.translate.instant('nav.network.schedules'),
              icon: SbIcons.schedules,
              link: RoutingID.schedules,
              minPermissions: [Permission.playback_schedule_list],
            },
            {
              name: this.translate.instant('nav.network.verification'),
              icon: SbIcons.bookingVerification,
              link: RoutingID.verification,
              minPermissions: [Permission.verification_list],
            },
            {
              name: this.translate.instant('nav.network.bookingsList'),
              icon: SbIcons.bookingList,
              link: RoutingID.networkBookingsList,
              minPermissions: [Permission.network_booking_list],
            },
            {
              name: this.translate.instant('nav.network.mediumTransformations'),
              icon: SbIcons.expand,
              link: RoutingID.mediumTransformationList,
              minPermissions: [Permission.device_show], // [Permission.mediumTransformationList],
            },
          ],
        },
        {
          name: this.translate.instant('nav.marketing'),
          icon: SbIcons.hammerAd,
          link: RoutingID.marketing,
          minPermissions: [Permission.network_share_list, Permission.deal_list],
          tooltipText: '', // this.translate.instant('nav.network.content.description'),
          children: [
            {
              name: this.translate.instant('nav.network.share'),
              icon: SbIcons.networkShare,
              link: RoutingID.networkShare,
              minPermissions: [Permission.network_share_list],
            },
            {
              name: this.translate.instant('nav.datasets.deals'),
              icon: SbIcons.budgetCoin,
              link: RoutingID.deals,
              minPermissions: [Permission.deal_list],
            },
          ],
        },
      ],
    };

    this.datasets = {
      name: this.translate.instant('nav.datasets'),
      icon: SbIcons.datasets,
      link: RoutingID.datasets,
      minPermissions: [
        Permission.company_list,
        Permission.tag_list,
        Permission.company_attribute_datatype_list,
        Permission.location_attribute_datatype_list,
        Permission.device_attribute_datatype_list,
        Permission.ssp_contract_list,
        Permission.campaign_type_list,
        Permission.product_category_list,
        Permission.template_group_list,
        Permission.template_source_list,
        Permission.channel_list,
        Permission.player_hardware_list,
        Permission.cull_list,
        Permission.area_list,
        // Permission.attachment_list,
        Permission.asset_repository_list,
        Permission.weekly_schedule_list,
      ],
      children: [
        {
          name: this.translate.instant('nav.applicationData'),
          icon: SbIcons.usbDevice,
          link: RoutingID.applicationData,
          minPermissions: [Permission.cull_list, Permission.area_list, Permission.attachment_list, Permission.weekly_schedule_list],
          tooltipText: this.translate.instant('nav.datasets.datasets.description'),
          children: [
            {
              name: this.translate.instant('nav.datasets.culls'),
              icon: SbIcons.selection,
              link: RoutingID.filters,
              minPermissions: [Permission.cull_list],
            },
            {
              name: this.translate.instant('nav.datasets.areas'),
              icon: SbIcons.globe,
              link: RoutingID.areas,
              minPermissions: [Permission.area_list],
            },
            /*
            {
              name: this.translate.instant('nav.attachmentExplorer'),
              icon: SbIcons.attachments,
              link: RoutingID.attachmentExplorer,
              minPermissions: [Permission.attachment_list],
            },
             */
            {
              name: this.translate.instant('nav.weeklySchedule'),
              icon: SbIcons.clock,
              link: RoutingID.weeklySchedule,
              minPermissions: [Permission.weekly_schedule_list],
            },
          ],
        },
        {
          name: this.translate.instant('nav.masterData'),
          icon: SbIcons.masterData,
          minPermissions: [
            Permission.company_list,
            Permission.tag_list,
            Permission.company_attribute_datatype_list,
            Permission.location_attribute_datatype_list,
            Permission.device_attribute_datatype_list,
            Permission.color_definition_list,
            // Permission.time_type_list
          ],
          tooltipText: this.translate.instant('nav.datasets.companies.description'),
          link: RoutingID.masterData,
          children: [
            {
              name: this.translate.instant('nav.companies.all'),
              icon: SbIcons.company,
              link: RoutingID.companies,
              minPermissions: [Permission.company_list],
            },
            {
              name: this.translate.instant('nav.datasets.tags'),
              icon: SbIcons.tags,
              link: RoutingID.tags,
              minPermissions: [Permission.tag_list],
            },
            {
              name: this.translate.instant('nav.datasets.attributes'),
              icon: SbIcons.attributes,
              link: RoutingID.attributes,
              minPermissions: [
                Permission.company_attribute_datatype_list,
                Permission.location_attribute_datatype_list,
                Permission.device_attribute_datatype_list,
              ],
            },
            {
              name: this.translate.instant('nav.datasets.colors'),
              icon: SbIcons.colorPalette,
              link: RoutingID.colors,
              minPermissions: [Permission.color_definition_list],
            },
            /*{
                                                                          name: this.translate.instant('nav.timeTypes'),
                                                                          icon: SbIcons.timeTypes,
                                                                          link: RoutingID.timeTypes,
                                                                          minPermissions: [
                                                                            Permission.time_type_list
                                                                          ],
                                                                        },

                                                                         */
          ],
        },
        {
          name: this.translate.instant('nav.network'),
          icon: SbIcons.network,
          link: RoutingID.datasetNetwork,
          minPermissions: [
            Permission.channel_list,
            Permission.player_hardware_list,
            Permission.player_software_list,
            Permission.screen_hardware_list,
            Permission.ssp_contract_list,
          ],
          tooltipText: this.translate.instant('nav.datasets.datasets.description'),
          children: [
            {
              name: this.translate.instant('nav.network.channels'),
              icon: SbIcons.stele,
              link: RoutingID.channels,
              minPermissions: [Permission.channel_list],
            },
            {
              name: this.translate.instant('nav.network.playerHardware'),
              icon: SbIcons.playerSetting,
              link: RoutingID.playerHardware,
              minPermissions: [Permission.player_hardware_list],
            },
            {
              name: this.translate.instant('nav.network.playerSoftware'),
              icon: SbIcons.playerSoftware,
              link: RoutingID.playerSoftware,
              minPermissions: [Permission.player_software_list],
            },
            {
              name: this.translate.instant('nav.network.screenHardware'),
              icon: SbIcons.screenSetting,
              link: RoutingID.screenHardware,
              minPermissions: [Permission.screen_hardware_list],
            },
            {
              name: this.translate.instant('nav.datasets.sspContracts'),
              icon: SbIcons.contract,
              link: RoutingID.sspContracts,
              minPermissions: [Permission.ssp_contract_list],
            },
          ],
        },
        {
          name: this.translate.instant('nav.content'),
          icon: SbIcons.megaphone,
          minPermissions: [
            Permission.campaign_type_list,
            Permission.product_category_list,
            Permission.template_group_list,
            Permission.template_source_list,
            Permission.asset_repository_list,
          ],
          link: RoutingID.datasetContent,
          tooltipText: this.translate.instant('nav.datasets.datasets.description'),
          children: [
            {
              name: this.translate.instant('nav.datasets.campaignTypes'),
              icon: SbIcons.megaphoneTypes,
              link: RoutingID.campaignTypes,
              minPermissions: [Permission.campaign_type_list],
            },
            {
              name: this.translate.instant('nav.productCategories'),
              icon: SbIcons.productCategory,
              link: RoutingID.productCategories,
              minPermissions: [Permission.product_category_list],
            },
            {
              name: this.translate.instant('nav.templateGroup'), // this.translate.instant('nav.motives.motiveGroups'),
              icon: SbIcons.templates,
              link: RoutingID.templateGroups,
              minPermissions: [Permission.template_group_list],
            },
            {
              name: this.translate.instant('nav.templateSources'),
              icon: SbIcons.template,
              link: RoutingID.templateSources,
              minPermissions: [Permission.template_source_list],
            },
            {
              name: this.translate.instant('nav.repositories'), // this.translate.instant('nav.motives.motiveGroups'),
              icon: SbIcons.folderPhoto,
              link: RoutingID.repositories,
              minPermissions: [Permission.asset_repository_list],
            },
          ],
        },
      ],
    };

    this.reporting = {
      name: this.translate.instant('nav.reporting'),
      icon: SbIcons.pieChart,
      minPermissions: [Permission.ds_cm_reporting, Permission.ss_nm_reporting, Permission.ss_fm_reporting, Permission.reporting_list],
      link: RoutingID.reporting,
      children: [
        {
          name: this.translate.instant('nav.reporting.content'),
          minPermissions: [Permission.ds_cm_reporting],
          tooltipText: this.translate.instant('nav.reporting.content.description'),
          icon: SbIcons.screenPlay,
          link: RoutingID.contentReporting,
          children: [
            {
              name: this.translate.instant('nav.reporting.content.shareOfVoice'),
              icon: SbIcons.pieChartStake,
              link: RoutingID.shareOfVoice,
              disabled: !this.userService.debugMode,
              minPermissions: [Permission.ds_cm_reporting],
            },
            {
              name: this.translate.instant('nav.reporting.advertiser.shareOfVoice'),
              icon: SbIcons.pieChartStake,
              link: RoutingID.advertiserShareOfVoice,
              minPermissions: [Permission.ds_cm_reporting],
            },
          ],
        },
        {
          name: this.translate.instant('nav.reporting.network'),
          minPermissions: [Permission.ss_nm_reporting, Permission.ss_fm_reporting],
          tooltipText: this.translate.instant('nav.reporting.network.description'),
          icon: SbIcons.network,
          link: RoutingID.networkReporting,
          children: [
            {
              name: this.translate.instant('nav.reporting.network.playerReporting'),
              icon: SbIcons.playerPieChart,
              link: RoutingID.playerReporting,
              minPermissions: [Permission.ss_nm_reporting, Permission.ss_fm_reporting],
            },
            {
              name: this.translate.instant('nav.reporting.network.locationReporting'),
              icon: SbIcons.locationStreet,
              link: RoutingID.locationReporting,
              minPermissions: [Permission.ss_nm_reporting, Permission.ss_fm_reporting],
            },
            {
              name: this.translate.instant('nav.reporting.network.airtimeReporting'),
              icon: SbIcons.barChart,
              link: RoutingID.airtimeReporting,
              minPermissions: [Permission.ss_nm_reporting],
            },
            {
              name: this.translate.instant('nav.reporting.publisher.shareOfVoice'),
              icon: SbIcons.pieChartStake,
              link: RoutingID.publisherShareOfVoice,
              minPermissions: [Permission.ss_nm_reporting],
            },
          ],
        },
        {
          name: this.translate.instant('nav.reporting.standardReporting'),
          minPermissions: [
            Permission.cron_definition_list,
            Permission.time_range_list,
            Permission.reporting_setup_list,
            Permission.reporting_type_list,
            Permission.reporting_template_list,
            Permission.reporting_list,
          ],
          tooltipText: this.translate.instant('nav.reporting.standardReporting.description'),
          icon: SbIcons.pieChartSettings,
          link: RoutingID.standardReporting,
          children: [
            {
              name: this.translate.instant('nav.reporting.generatedReports'),
              icon: SbIcons.pieChartList,
              link: RoutingID.generatedReport,
              minPermissions: [Permission.reporting_list],
              disabled: false,
            },
          ],
        },
      ],
    };
    const main: SbNavpointModel[] = [this.dashboard, this.content, this.network, this.datasets, this.reporting];

    const footer = [
      {
        name: this.translate.instant('nav.support'),
        icon: SbIcons.userManagement,
        link: 'support',
      },
      {
        name: this.translate.instant('nav.imprint'),
        icon: SbIcons.company,
        link: 'imprint',
      },
    ];
    const map: Map<string, SbNavpointModel[]> = new Map();
    map.set('main', main);
    map.set('footer', this.getFooter());
    return map;
  }

  getAdminNavpoints(signedIn: boolean = false): Map<string, SbNavpointModel[]> {
    let main = [
      {
        name: this.translate.instant('nav.admin'),
        icon: 'security',
        link: RoutingID.admin,
      },
    ];

    let authed = [];

    if (signedIn) {
      authed = [
        {
          name: this.translate.instant('nav.admin.users'),
          icon: SbIcons.userManagement,
          link: RoutingID.adminUserManagement,
        },
        {
          name: this.translate.instant('nav.admin.clients'),
          icon: SbIcons.companyClient,
          link: RoutingID.adminClientManagement,
        },
        {
          name: this.translate.instant('nav.admin.queryPerformance'),
          icon: SbIcons.lineChart,
          link: RoutingID.queryPerformance,
        },
        {
          name: this.translate.instant('nav.admin.endpointPerformance'),
          icon: SbIcons.lineChart,
          link: RoutingID.endpointPerformance,
        },
        {
          name: this.translate.instant('nav.admin.userMembership'),
          icon: SbIcons.users,
          link: RoutingID.userMembership,
        },
        {
          name: 'Backgroundworker',
          icon: SbIcons.settings,
          link: RoutingID.backgroundWorker,
        },
      ];
    }

    main = main.concat(authed);

    const map: Map<string, SbNavpointModel[]> = new Map();
    map.set('main', main);
    map.set('footer', this.getFooter());
    return map;
  }

  getFooter(): SbNavpointModel[] {
    return [
      {
        name: this.translate.instant('nav.support'),
        icon: SbIcons.userManagement,
        link: 'support',
      },
      {
        name: this.translate.instant('nav.imprint'),
        icon: SbIcons.company,
        link: 'imprint',
      },
    ];
  }
}
